import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ScatterChart, Scatter, ZAxis } from "recharts";
import { Flame, TrendingDown, TrendingUp, Minus, MousePointerClick, X } from "lucide-react";
import BodyMap, { AREA_LABELS, heatmapColor, heatmapStroke } from "@/components/BodyMap";
import BodyRegionHoverChart from "@/components/BodyRegionHoverChart";

const AREA_DISPLAY = (a) => AREA_LABELS?.[a] || (a || "").replace(/_/g, " ");

// A tiny personal-view popup mirroring the clinic manager's design but
// scoped to a single sonographer. Sits above the body model with a soft
// amber-tinted backing circle whose color reflects that region's own pain.
function PersonalImpactPopup({ area, avg, reports, byBodyPart, onClose }) {
  const base = area ? area.replace(/_(left|right)$/, "") : null;
  const stats = base ? byBodyPart?.[base] : null;
  const bg = heatmapColor(avg);
  const stroke = heatmapStroke(avg);
  const label = AREA_LABELS?.[area] || (base || area || "").replace(/_/g, " ");

  const chartData = (stats?.series || []).map((r) => ({
    date: r.date.slice(5), pain: r.pain, stretch_min: r.stretch_min,
  }));

  return (
    <div className="relative w-full flex justify-center mb-6" data-testid="sono-impact-popup">
      <div
        aria-hidden="true"
        className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-full pointer-events-none"
        style={{
          width: "min(560px, 92%)",
          height: 400,
          background: `radial-gradient(circle at 50% 40%, ${bg} 0%, ${bg}dd 55%, ${bg}55 85%, transparent 100%)`,
          filter: "blur(2px)",
        }}
      />
      <div
        className="relative bg-white/85 backdrop-blur-md border-2 rounded-3xl shadow-2xl w-full max-w-xl mx-auto"
        style={{ borderColor: stroke, boxShadow: `0 20px 40px ${stroke}33` }}
      >
        <div className="flex items-start justify-between px-5 pt-4 pb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold" style={{ color: stroke }}>
              Focused pain point
            </div>
            <h3 className="text-xl font-black mt-0.5 capitalize" style={{ color: stroke }}>
              {label}
            </h3>
            {avg != null && (
              <div className="mt-1 text-xs text-zinc-700">
                <span className="font-bold">{avg}</span>
                <span className="text-zinc-500"> avg pain · {reports} {reports === 1 ? "log" : "logs"} · last 30 days</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close focused region"
            data-testid="sono-impact-popup-close"
            className="text-zinc-500 hover:text-zinc-900 rounded-full p-1 hover:bg-white/60 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {chartData.length > 0 ? (
          <div className="px-3 pb-2">
            <ResponsiveContainer width="100%" height={170}>
              <LineChart data={chartData} margin={{ top: 8, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#f4f4f5" strokeOpacity={0.4} />
                <XAxis dataKey="date" stroke="#71717a" fontSize={9} interval="preserveStartEnd" />
                <YAxis yAxisId="pain" domain={[0, 10]} stroke="#71717a" fontSize={9} width={22} />
                <YAxis yAxisId="stretch" orientation="right" stroke={stroke} fontSize={9} width={22} />
                <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
                <Line yAxisId="pain" type="monotone" dataKey="pain" name="Pain" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 2, fill: "#0f172a" }} connectNulls />
                <Line yAxisId="stretch" type="monotone" dataKey="stretch_min" name="Stretching (min)" stroke={stroke} strokeWidth={2.5} dot={{ r: 2, fill: stroke }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-zinc-600 pt-1 pl-3">
              Black = your daily pain · amber = clinic stretching minutes for this region.
            </p>
          </div>
        ) : (
          <div className="px-5 py-6 text-sm text-zinc-700 text-center">
            No logs yet for <span className="font-semibold">{label}</span>. Keep checking in to build your trend.
          </div>
        )}
      </div>
    </div>
  );
}

export default function SonographerStatistics() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entryLimit, setEntryLimit] = useState(3);
  const [selectedArea, setSelectedArea] = useState(null);

  useEffect(() => {
    (async () => {
      const [s, e] = await Promise.all([api.get("/pain-entries/summary"), api.get("/pain-entries")]);
      setSummary(s.data); setEntries(e.data);
    })();
  }, []);

  const scatter = entries.map((e) => ({ hours: e.hours_scanned, pain: e.overall_score, date: e.created_at?.slice(0, 10) }));

  const byBodyPart = summary?.by_body_part || {};
  const treatments = summary?.temperature_therapy_by_base || {};

  const heatmap = useMemo(() => {
    const m = {};
    (summary?.hotspots || []).forEach((h) => { m[h.area] = { avg: h.avg, reports: h.reports }; });
    return m;
  }, [summary]);

  const selectedInfo = selectedArea ? heatmap[selectedArea] : null;

  // Top 3 hotspots (last 30 days), with MoM delta vs the prior 30
  const hotspots = useMemo(() => {
    if (!entries?.length) return [];
    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const acc = {};
    for (const e of entries) {
      const t = new Date(e.created_at).getTime();
      const ageDays = (now - t) / day;
      const bucket = ageDays < 30 ? "recent" : ageDays < 60 ? "prior" : null;
      if (!bucket) continue;
      for (const loc of e.locations || []) {
        if (!acc[loc.area]) acc[loc.area] = { recent: [], prior: [] };
        acc[loc.area][bucket].push(loc.score);
      }
    }
    const mean = (arr) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0);
    const rows = Object.entries(acc)
      .filter(([, v]) => v.recent.length > 0)
      .map(([area, v]) => ({
        area,
        avg: +mean(v.recent).toFixed(1),
        reports: v.recent.length,
        delta: +(mean(v.recent) - mean(v.prior)).toFixed(1),
        hasPrior: v.prior.length > 0,
      }))
      .sort((a, b) => b.avg - a.avg || b.reports - a.reports);
    return rows.slice(0, 3);
  }, [entries]);

  return (
    <div>
      <span className="pill">Statistics</span>
      <h1 className="mt-2 text-3xl font-bold">Your data, in depth.</h1>
      <p className="text-zinc-500 mt-1">Pain trends, injury location analytics and workload correlations.</p>

      {/* Top hotspots this month */}
      <div className="mt-8" data-testid="hotspots-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Flame className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
            Top hotspots · last 30 days
          </h2>
          {hotspots.length > 0 && (
            <span className="text-xs text-zinc-500">Δ shown vs previous 30 days</span>
          )}
        </div>
        {hotspots.length === 0 ? (
          <div className="card-elev text-sm text-zinc-500" data-testid="hotspots-empty">
            No painful regions logged in the last 30 days. Keep checking in to surface your trends.
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-4">
            {hotspots.map((h, i) => {
              const trend = !h.hasPrior ? "new" : h.delta > 0.3 ? "up" : h.delta < -0.3 ? "down" : "flat";
              const trendStyle = {
                up:   { Icon: TrendingUp,   color: "#dc2626", text: `+${h.delta} vs prev` },
                down: { Icon: TrendingDown, color: "#16a34a", text: `${h.delta} vs prev` },
                flat: { Icon: Minus,        color: "#71717a", text: "no change" },
                new:  { Icon: Flame,        color: "#d4af37", text: "new this period" },
              }[trend];
              const { Icon } = trendStyle;
              return (
                <div key={h.area} className="card-elev relative" data-testid={`hotspot-${i + 1}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-zinc-400">#{i + 1} HOTSPOT</div>
                      <div className="mt-1 text-lg font-bold capitalize">{AREA_DISPLAY(h.area)}</div>
                    </div>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                      style={{ background: "#fafafa", color: trendStyle.color, border: `1px solid ${trendStyle.color}22` }}
                      data-testid={`hotspot-${i + 1}-trend`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {trendStyle.text}
                    </span>
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-3xl font-black" style={{ color: "var(--gold-hover)" }}>{h.avg}</span>
                    <span className="text-xs text-zinc-500">avg pain · {h.reports} {h.reports === 1 ? "log" : "logs"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* HERO: personal pain model, same UX as the clinic view but scoped to
          the sonographer's own data. */}
      <section className="mt-12" data-testid="sono-body-map-section">
        <div className="text-center mb-4">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500">Your pain map · last 30 days</div>
          <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-zinc-500">
            <MousePointerClick className="w-3.5 h-3.5" />
            Hover for a quick preview · tap to open the full impact chart
          </div>
        </div>

        {selectedArea && (
          <PersonalImpactPopup
            area={selectedArea}
            avg={selectedInfo?.avg ?? null}
            reports={selectedInfo?.reports ?? 0}
            byBodyPart={byBodyPart}
            onClose={() => setSelectedArea(null)}
          />
        )}

        <div className="mx-auto" style={{ maxWidth: 640 }}>
          <BodyMap
            heatmap={heatmap}
            onRegionClick={(a) => setSelectedArea(a)}
            activeArea={selectedArea}
            large
            treatments={treatments}
            title="Your pain model"
            subtitle="Hover for a quick preview · dashed ring = temperature therapy this month"
            renderHoverContent={(area) => (
              <BodyRegionHoverChart
                area={area}
                byBodyPart={byBodyPart}
                treatments={treatments}
                hasData={!!byBodyPart[area?.replace(/_(left|right)$/, "")]}
              />
            )}
          />
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div className="card-elev">
          <h3 className="font-bold mb-4">Pain trend (last 30)</h3>
          <p className="text-xs text-zinc-500 -mt-3 mb-3">Daily average across all painful regions you logged.</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={summary?.trend || []}>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
              <YAxis domain={[0, 10]} stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
              <Line type="monotone" dataKey="score" stroke="#d4af37" strokeWidth={3} dot={{ r: 3, fill: "#d4af37" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-elev">
          <h3 className="font-bold mb-4">Hours scanned vs pain</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={summary?.trend || []}>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
              <Line type="monotone" dataKey="hours" stroke="#09090b" strokeWidth={2} />
              <Line type="monotone" dataKey="score" stroke="#d4af37" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-elev">
          <h3 className="font-bold mb-4">Pain by region (average)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary?.by_area || []}>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis dataKey="area" stroke="#71717a" fontSize={11} />
              <YAxis domain={[0, 10]} stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
              <Bar dataKey="avg" fill="#09090b" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-elev">
          <h3 className="font-bold mb-4">Workload correlation (hours × pain)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis type="number" dataKey="hours" name="Hours" stroke="#71717a" fontSize={11} />
              <YAxis type="number" dataKey="pain" name="Pain" domain={[0, 10]} stroke="#71717a" fontSize={11} />
              <ZAxis range={[60, 60]} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
              <Scatter data={scatter} fill="#d4af37" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card-elev mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Recent entries</h3>
          {entries.length > 0 && <span className="text-xs text-zinc-500">{entries.length} total</span>}
        </div>
        {entries.length === 0 ? (
          <div className="text-sm text-zinc-500">No entries yet.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500">
                  <tr><th className="py-2">Date</th><th>Avg pain</th><th>Hours</th><th>Patients</th><th>Areas</th></tr>
                </thead>
                <tbody>
                  {entries.slice(0, entryLimit).map((e) => (
                    <tr key={e.entry_id} className="border-t border-zinc-100" data-testid={`stat-row-${e.entry_id}`}>
                      <td className="py-2">{e.created_at?.slice(0, 10)}</td>
                      <td className="font-semibold">{e.overall_score}</td>
                      <td>{e.hours_scanned}h</td>
                      <td>{e.patients_scanned}</td>
                      <td className="text-zinc-600">{e.locations?.map((l) => l.area).join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {entries.length > entryLimit && (
              <div className="mt-4 flex justify-center gap-3">
                <button onClick={() => setEntryLimit((n) => n + 10)} className="btn-outline text-sm h-9 px-4" data-testid="stat-entries-load-more">
                  Load 10 more · {entries.length - entryLimit} hidden
                </button>
                <button onClick={() => setEntryLimit(entries.length)} className="btn-ghost text-sm h-9 px-4" data-testid="stat-entries-show-all">
                  Show all
                </button>
              </div>
            )}
            {entryLimit > 3 && entries.length > 3 && (
              <div className="mt-2 flex justify-center">
                <button onClick={() => setEntryLimit(3)} className="btn-ghost text-xs h-8" data-testid="stat-entries-collapse">
                  Collapse to 3
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
